/* oxlint-disable anti-slop/no-shape-in-symbol-names -- legacy artifacts and ChartMarker expose markerShape/shape as public wire fields */
import type { ChartMarker, ChartRow, ChartSeries, ChartSpec } from './types';
import { chartTimeMilliseconds } from './timeRange';
import { CHART_SPEC_CONTRACT_HASH, CHART_SPEC_VERSION } from './contract';

type BacktestPoint = {
  time?: string | number;
  value?: number;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  detailText?: string;
  side?: string;
  price?: number;
  status?: string;
  symbol?: string;
  assetType?: string;
  assetStrike?: string | number;
  assetRight?: string;
  assetExpiration?: string;
  assetMultiplier?: string | number;
  multiplier?: string | number;
  orderType?: string;
  orderAction?: string;
  strategy?: string;
  identifier?: string;
  quantity?: number;
  qty?: number;
  tradeDetails?: {
    tradeQuantity?: string | number | null;
    tradeCost?: string | number | null;
    timeInForce?: string | null;
    positionBefore?: string | number | null;
    positionAfter?: string | number | null;
    openingQuantity?: string | number | null;
    closingQuantity?: string | number | null;
    positionAction?: string | null;
  };
};

type BacktestSeries = {
  name?: string;
  type?: string;
  panel?: string;
  unit?: string;
  color?: string;
  markerShape?: 'arrowUp' | 'arrowDown' | 'circle' | 'square';
  markerPlacement?: 'above' | 'below' | 'at';
  compactMarker?: boolean;
  assetSymbol?: string;
  points?: BacktestPoint[];
};

type SemanticPane = 'price' | 'equity' | 'oscillators';
type ChartPaneIdentity = { id: string; label?: string };
type SemanticPaneIdentity = { id: string; label: string };

const DEFAULT_LEGACY_PANELS = new Set(['', 'default', 'default_plot', 'main', 'price']);

function compactSignalLabel(name: string | undefined): string {
  const label = name?.trim() || 'Signal';
  const normalized = label.toLowerCase();
  if (normalized.startsWith('buy ')) return 'Buy';
  if (normalized.startsWith('sell ')) return 'Sell';
  return label;
}

type TradeAction = NonNullable<ChartMarker['action']>;
type BacktestScalar = string | number | boolean | null | undefined;

const TRADE_ACTIONS = {
  buy: { label: 'Buy', side: 'buy' },
  sell: { label: 'Sell', side: 'sell' },
  'sell-short': { label: 'Sell short', side: 'sell' },
  'buy-to-cover': { label: 'Buy to cover', side: 'buy' },
} satisfies Record<TradeAction, { label: string; side: 'buy' | 'sell' }>;

function finiteNumber(value: BacktestScalar): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const number = Number(String(value).replace(/[$,]/g, ''));
  return Number.isFinite(number) ? number : undefined;
}

function humanizeIdentifier(value: BacktestScalar): string | undefined {
  const normalized = String(value || '')
    .replace(/<br\s*\/?\s*>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/_+/g, ' ')
    .replace(/\s+-\s+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return normalized || undefined;
}

function humanizeStatus(value: BacktestScalar): string | undefined {
  const status = humanizeIdentifier(value);
  if (!status) return undefined;
  const normalized = status.toLowerCase();
  if (normalized === 'fill' || normalized === 'filled') return 'Filled';
  return status.replace(/^\w/, (letter) => letter.toUpperCase());
}

function humanizeTradeAction(value: BacktestScalar): string | undefined {
  const action = humanizeIdentifier(value);
  if (!action) return undefined;
  const normalized = action.toLowerCase();
  if (normalized === 'buy') return 'Buy';
  if (normalized === 'sell') return 'Sell';
  if (normalized === 'buy to open') return 'Buy to open';
  if (normalized === 'sell to open') return 'Sell to open';
  if (normalized === 'buy to close') return 'Buy to close';
  if (normalized === 'sell to close') return 'Sell to close';
  if (normalized === 'buy to cover') return 'Buy to cover';
  if (normalized === 'sell short') return 'Sell short';
  if (normalized === 'open long') return 'Open long';
  if (normalized === 'close long') return 'Close long';
  if (normalized === 'open short') return 'Open short';
  if (normalized === 'close short') return 'Close short';
  if (normalized === 'reverse to long') return 'Reverse to long';
  if (normalized === 'reverse to short') return 'Reverse to short';
  return action.replace(/^\w/, (letter) => letter.toUpperCase());
}

function parsedDetailFields(detailText: string | undefined): Record<string, string> {
  if (!detailText) return {};
  return detailText
    .replace(/<br\s*\/?\s*>/gi, '|')
    .split('|')
    .map((part) => part.trim())
    .reduce<Record<string, string>>((fields, part) => {
      const separator = part.indexOf('=');
      if (separator <= 0) return fields;
      const key = part.slice(0, separator).trim().toLowerCase();
      const value = part.slice(separator + 1).trim();
      if (key && value) fields[key] = value;
      return fields;
    }, {});
}

function normalizedTradeAction(...candidates: BacktestScalar[]): TradeAction | undefined {
  const text = candidates
    .map((candidate) =>
      String(candidate || '')
        .trim()
        .toLowerCase(),
    )
    .find(Boolean);
  if (!text) return undefined;
  const normalized = text.replace(/^filled\s+/, '').replace(/[\s_]+/g, '-');
  if (normalized.includes('reverse-to-long')) return 'buy-to-cover';
  if (normalized.includes('reverse-to-short')) return 'sell-short';
  if (normalized.includes('buy-to-cover') || normalized.includes('close-short'))
    return 'buy-to-cover';
  if (normalized.includes('sell-short') || normalized.includes('open-short')) return 'sell-short';
  if (normalized.startsWith('buy') || normalized.includes('open-long')) return 'buy';
  if (normalized.startsWith('sell') || normalized.includes('close-long')) return 'sell';
  return undefined;
}

function markerFromLegacyPoint(
  signal: BacktestSeries,
  point: BacktestPoint,
  signalIndex: number,
  pointIndex: number,
  markerSeries: ChartSeries,
  chartTimes: Array<string | number>,
): ChartMarker & { __origin: 'artifact' | 'trade' } {
  const fields = parsedDetailFields(point.detailText);
  const rawAction =
    point.tradeDetails?.positionAction || point.orderAction || fields.action || point.side;
  const action = normalizedTradeAction(rawAction, signal.name);
  const tradeAction = action ? TRADE_ACTIONS[action] : undefined;
  const nameFillMatch = String(signal.name || '')
    .trim()
    .match(/^(?:filled\s+)?(?:buy|sell)(?:_short|_to_cover)?\s+([0-9.]+)\s+([A-Za-z0-9:/.-]+)/i);
  const quantity = finiteNumber(
    point.tradeDetails?.tradeQuantity ??
      point.quantity ??
      point.qty ??
      fields.quantity ??
      nameFillMatch?.[1],
  );
  const price = finiteNumber(point.price ?? fields.price ?? point.value ?? point.close);
  const status = humanizeStatus(
    point.status ||
      fields.status ||
      (/\bfilled\b/i.test(String(signal.name || '')) ||
      /\bfilled\b/i.test(String(point.detailText || ''))
        ? 'Filled'
        : ''),
  );
  const reason = humanizeIdentifier(fields.reason);
  const symbol =
    String(point.symbol || signal.assetSymbol || nameFillMatch?.[2] || '').trim() || undefined;
  const namedPositionAction = String(signal.name || '')
    .trim()
    .match(/^(open|close|reverse)\s+(long|short)(?:\b|$)/i)?.[0];
  const actionLabel = humanizeTradeAction(rawAction || namedPositionAction || tradeAction?.label);
  const label = actionLabel
    ? actionLabel
    : tradeAction?.label ||
      (signal.compactMarker ? compactSignalLabel(signal.name) : signal.name || 'Signal');
  const detailParts = [
    status,
    tradeAction?.label,
    quantity !== undefined ? String(quantity) : undefined,
    symbol,
    price !== undefined
      ? `at $${price.toLocaleString(undefined, { maximumFractionDigits: 8 })}`
      : undefined,
    reason ? `Reason: ${reason}` : undefined,
  ].filter(Boolean);
  const detail =
    signal.type === 'trade' && point.detailText && !/[<|]|\w+=/.test(point.detailText)
      ? humanizeIdentifier(point.detailText)
      : detailParts.length > 0
        ? detailParts.join(' ')
        : humanizeIdentifier(point.detailText);
  const pointTime = point.time ?? chartTimes[0] ?? 0;
  const markerValue = finiteNumber(point.value ?? point.close);

  return {
    id: `signal_${signalIndex + 1}_${pointIndex + 1}`,
    paneId: markerSeries.paneId,
    seriesId: markerSeries.id,
    x: nearestChartTime(pointTime, chartTimes),
    value: markerValue,
    placement:
      tradeAction?.side === 'buy'
        ? 'below'
        : tradeAction?.side === 'sell'
          ? 'above'
          : signal.markerPlacement || 'at',
    shape:
      tradeAction?.side === 'buy'
        ? 'arrowUp'
        : tradeAction?.side === 'sell'
          ? 'arrowDown'
          : signal.markerShape || 'circle',
    color:
      tradeAction?.side === 'buy'
        ? '#22C55E'
        : tradeAction?.side === 'sell'
          ? '#EF4444'
          : signal.color || '#F59E0B',
    label,
    detail,
    compact: Boolean(tradeAction || signal.compactMarker),
    side: tradeAction?.side,
    action,
    details: tradeAction
      ? {
          status,
          symbol,
          description: signal.type === 'trade' && detail !== label ? detail : undefined,
          quantity,
          price,
          reason,
          actionLabel,
          filledAt: point.time,
          assetType: humanizeIdentifier(point.assetType),
          optionType: humanizeIdentifier(point.assetRight),
          strike: finiteNumber(point.assetStrike),
          expiration: point.assetExpiration || undefined,
          multiplier: finiteNumber(point.assetMultiplier ?? point.multiplier),
          orderType: humanizeIdentifier(point.orderType),
          timeInForce: humanizeIdentifier(point.tradeDetails?.timeInForce),
          tradeValue: finiteNumber(point.tradeDetails?.tradeCost),
          strategy: humanizeIdentifier(point.strategy),
          identifier: humanizeIdentifier(point.identifier),
          positionBefore: finiteNumber(point.tradeDetails?.positionBefore),
          positionAfter: finiteNumber(point.tradeDetails?.positionAfter),
          openingQuantity: finiteNumber(point.tradeDetails?.openingQuantity),
          closingQuantity: finiteNumber(point.tradeDetails?.closingQuantity),
        }
      : undefined,
    __origin: signal.type === 'marker' ? 'artifact' : 'trade',
  };
}

function mergeTradeMarkers(
  markers: Array<ChartMarker & { __origin: 'artifact' | 'trade' }>,
): ChartMarker[] {
  const result: Array<ChartMarker & { __origin: 'artifact' | 'trade' }> = [];
  const crossSourceIndex = new Map<string, number>();
  markers.forEach((marker) => {
    if (!marker.side) {
      result.push(marker);
      return;
    }
    const quantity = marker.details?.quantity;
    const price = marker.details?.price ?? marker.value;
    const key = [
      marker.side,
      String(marker.x),
      Number.isFinite(Number(price)) ? Number(price).toFixed(6) : '',
      Number.isFinite(Number(quantity)) ? Number(quantity).toPrecision(12) : '',
    ].join('|');
    const existingIndex = crossSourceIndex.get(key);
    const existing = existingIndex === undefined ? undefined : result[existingIndex];
    if (existingIndex === undefined || !existing || existing.__origin === marker.__origin) {
      crossSourceIndex.set(key, result.length);
      result.push(marker);
      return;
    }
    const specificAction =
      marker.action && marker.action !== marker.side ? marker.action : existing.action;
    const action = specificAction || marker.action || existing.action;
    const actionMetadata = action ? TRADE_ACTIONS[action] : undefined;
    const specificActionLabel =
      marker.action && marker.action !== marker.side
        ? marker.details?.actionLabel || marker.label
        : existing.action && existing.action !== existing.side
          ? existing.details?.actionLabel || existing.label
          : undefined;
    result[existingIndex] = {
      ...existing,
      ...marker,
      id: existing.id,
      action,
      side: actionMetadata?.side || marker.side || existing.side,
      label: actionMetadata?.label || marker.label || existing.label,
      details: {
        ...existing.details,
        ...Object.fromEntries(
          Object.entries(marker.details || {}).filter(([, value]) => value !== undefined),
        ),
        actionLabel:
          specificActionLabel ||
          marker.details?.actionLabel ||
          existing.details?.actionLabel ||
          actionMetadata?.label,
      },
      detail: [existing.detail, marker.detail]
        .filter(Boolean)
        .sort((left, right) => String(right).length - String(left).length)[0],
      __origin: 'trade',
    };
  });
  return result.map(({ __origin: _origin, ...marker }) => marker);
}

function semanticPaneForSeries(item: BacktestSeries): SemanticPane | null {
  const name = String(item.name || '')
    .trim()
    .toLowerCase();
  if (/\b(equity|portfolio value|account value|cash balance|net liquidation)\b/.test(name)) {
    return 'equity';
  }
  if (/\b(rsi\d*|stoch(?:astic)?\d*|williams\s*%?r|cci\d*|mfi\d*|adx\d*)\b/.test(name)) {
    return 'oscillators';
  }
  if (item.type === 'ohlc' || item.type === 'candlestick') return 'price';
  if (/\b(ema|sma|wma|vwap|price|close|open|high|low)\d*\b/.test(name)) return 'price';
  return null;
}

function stableId(prefix: string, index: number): string {
  return `${prefix}_${index + 1}`;
}

export function nearestChartTime(
  rawTime: string | number,
  chartTimes: Array<string | number>,
): string | number {
  const target = chartTimeMilliseconds(rawTime);
  const candidates = chartTimes
    .map((value) => ({ value, millis: chartTimeMilliseconds(value) }))
    .filter(
      (candidate): candidate is { value: string | number; millis: number } =>
        candidate.millis !== null,
    )
    .sort((left, right) => left.millis - right.millis);
  if (target === null || candidates.length === 0) return rawTime;

  const nearest = candidates.reduce((best, candidate) =>
    Math.abs(candidate.millis - target) < Math.abs(best.millis - target) ? candidate : best,
  );
  const intervals = candidates
    .slice(1)
    .map((candidate, index) => candidate.millis - candidates[index].millis)
    .filter((interval) => interval > 0)
    .sort((left, right) => left - right);
  const medianInterval = intervals.length > 0 ? intervals[Math.floor(intervals.length / 2)] : 0;
  const maximumDistance = Math.max(5 * 60 * 1000, medianInterval);
  return Math.abs(nearest.millis - target) <= maximumDistance ? nearest.value : rawTime;
}

export function backtestDataToChartSpec(
  indicatorData: BacktestSeries[],
  signalData: BacktestSeries[] = [],
): ChartSpec | null {
  if (!Array.isArray(indicatorData) || indicatorData.length === 0) return null;

  const rowsByTime = new Map<string | number, ChartRow>();
  const paneIds = new Map<string, string>();
  const series: ChartSeries[] = [];
  const selectedAssetSymbols = [
    ...new Set(indicatorData.map((item) => String(item.assetSymbol || '').trim()).filter(Boolean)),
  ];
  const isMultiSymbol = selectedAssetSymbols.length > 1;
  const primarySeriesBySymbol = new Map<string, ChartSeries>();
  const primaryPanelName =
    indicatorData.find(
      (item) => (item.type === 'ohlc' || item.type === 'candlestick') && item.panel,
    )?.panel || indicatorData.find((item) => item.panel)?.panel;
  const panelIdByName = new Map<string, string>();

  const symbolPane = (
    item: BacktestSeries,
    semanticPane: SemanticPane,
    label: string,
  ): { id: string; label: string } | null => {
    const assetSymbol = String(item.assetSymbol || '').trim();
    if (!isMultiSymbol || !assetSymbol) return null;
    const symbolId = assetSymbol
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
    return {
      id: `${symbolId}_${semanticPane}_pane`,
      label: `${assetSymbol} · ${label}`,
    };
  };

  const resolvePane = (item: BacktestSeries): ChartPaneIdentity => {
    const panelName = String(item.panel || '').trim();
    const shouldInferSemantics = DEFAULT_LEGACY_PANELS.has(panelName.toLowerCase());
    const semanticPane = shouldInferSemantics ? semanticPaneForSeries(item) : null;
    if (semanticPane) {
      const semanticPanes = {
        price: { id: 'primary_pane', label: 'Price' },
        equity: { id: 'equity_pane', label: 'Equity' },
        oscillators: { id: 'oscillator_pane', label: 'Oscillators' },
      } satisfies Record<SemanticPane, SemanticPaneIdentity>;
      return (
        symbolPane(item, semanticPane, semanticPanes[semanticPane].label) ||
        semanticPanes[semanticPane]
      );
    }
    if (!panelName || panelName === primaryPanelName) {
      return (
        symbolPane(item, 'price', panelName || 'Price') || {
          id: 'primary_pane',
          label: panelName || 'Price',
        }
      );
    }
    const existing = panelIdByName.get(panelName);
    if (existing) return { id: existing, label: panelName };
    const id = `indicator_pane_${panelIdByName.size + 1}`;
    panelIdByName.set(panelName, id);
    return { id, label: panelName };
  };

  indicatorData.forEach((item, index) => {
    const points = Array.isArray(item.points) ? item.points : [];
    const id = stableId('series', index);
    const pane = resolvePane(item);
    const paneId = pane.id;
    if (!paneIds.has(paneId)) paneIds.set(paneId, pane.label || paneId);
    const type = item.type;
    const isCandlestick = type === 'ohlc' || type === 'candlestick';
    const dataKey = `value_${index + 1}`;
    const fieldMapping = {
      open: `open_${index + 1}`,
      high: `high_${index + 1}`,
      low: `low_${index + 1}`,
      close: `close_${index + 1}`,
    };

    points.forEach((point) => {
      if (point.time === undefined || point.time === null) return;
      const row = rowsByTime.get(point.time) || { time: point.time };
      if (isCandlestick) {
        row[fieldMapping.open] = Number(point.open);
        row[fieldMapping.high] = Number(point.high);
        row[fieldMapping.low] = Number(point.low);
        row[fieldMapping.close] = Number(point.close);
      } else {
        row[dataKey] = Number(point.value);
      }
      rowsByTime.set(point.time, row);
    });

    const assetSymbol = String(item.assetSymbol || '').trim();
    const baseLabel = item.name || `Series ${index + 1}`;
    const displayLabel =
      isMultiSymbol && assetSymbol
        ? `${assetSymbol} · ${baseLabel === assetSymbol && isCandlestick ? 'Price' : baseLabel}`
        : baseLabel;
    const chartSeries: ChartSeries = {
      id,
      layerId: id,
      label: displayLabel,
      mark: isCandlestick ? 'candlestick' : type === 'bar' ? 'bar' : 'line',
      role: isCandlestick || semanticPaneForSeries(item) === 'price' ? 'price' : 'indicator',
      paneId,
      axisId: `${paneId}_axis`,
      dataKey: isCandlestick ? undefined : dataKey,
      fieldMapping: isCandlestick ? fieldMapping : undefined,
      style: {},
      unit: item.unit || 'value',
      frequency: 'provided',
      timezone: 'UTC',
      sourceIds: ['backtest-data'],
      showLastValue: true,
    };
    series.push(chartSeries);
    if (assetSymbol && (isCandlestick || !primarySeriesBySymbol.has(assetSymbol))) {
      primarySeriesBySymbol.set(assetSymbol, chartSeries);
    }
  });

  const data = [...rowsByTime.values()].sort((left, right) => {
    const leftTime = chartTimeMilliseconds(left.time);
    const rightTime = chartTimeMilliseconds(right.time);
    if (leftTime !== null && rightTime !== null) {
      return leftTime - rightTime;
    }
    return String(left.time).localeCompare(String(right.time));
  });
  const panes = [...paneIds].map(([id, label]) => ({
    id,
    label: label === id ? undefined : label,
    weight:
      id === 'primary_pane' || id.endsWith('_price_pane')
        ? 4
        : id === 'equity_pane' || id.endsWith('_equity_pane')
          ? 2
          : id === 'oscillator_pane' || id.endsWith('_oscillators_pane')
            ? 1.5
            : 1,
    axisIds: [`${id}_axis`],
  }));
  const chartTimes = data
    .map((row) => row.time)
    .filter((time): time is string | number => time !== undefined && time !== null);
  const markerSeries =
    series.find((item) => item.mark === 'candlestick') ||
    series.find((item) => item.role === 'price') ||
    series[0];
  const markers = mergeTradeMarkers(
    signalData.flatMap((signal, signalIndex) =>
      (signal.points || [])
        .filter((point) => point.time !== undefined && point.time !== null)
        .map((point, pointIndex) =>
          markerFromLegacyPoint(
            signal,
            point,
            signalIndex,
            pointIndex,
            primarySeriesBySymbol.get(String(signal.assetSymbol || point.symbol || '').trim()) ||
              markerSeries,
            chartTimes,
          ),
        ),
    ),
  ).map((marker) => ({ ...marker, layerId: 'trade_markers' }));

  return {
    schemaVersion: CHART_SPEC_VERSION,
    contractHash: CHART_SPEC_CONTRACT_HASH,
    kind: 'timeseries',
    title: 'Strategy indicators',
    xAxis: { key: 'time', type: 'time' },
    axes: panes.map((pane) => ({
      id: pane.axisIds[0],
      side: 'right',
      format: 'number',
    })),
    panes,
    layers: [
      ...series.map((item) => ({
        id: item.layerId!,
        label: item.label,
        kind: 'data' as const,
        description: `${item.label} data series`,
        style: item.style,
      })),
      ...(markers.length > 0
        ? [
            {
              id: 'trade_markers',
              label: 'Trades',
              kind: 'marker' as const,
              description: 'Executed strategy trades',
            },
          ]
        : []),
    ],
    series,
    data,
    referenceLines: [],
    markers,
  };
}
